import { EventMonthAhead, EventMonthAheadHeaders, EventWeekAhead, EventWeekAheadHeaders, MacroEventWeekAhead, MacroEventMonthAhead, MacroEventMonthAheadHeaders, EventsCallDetails, EventTypesResponse } from "@/models/events";
import { List, ListQueryParams } from "@/models/table";
import axios from "axios";
import dayjs from "dayjs";

export const getEventsByMonth = async (universeId: number, params: { start?: number; length?: number; month_of_year: string; event_types: string }) => {
    try {
        const res = await axios.get<List<EventMonthAhead, EventMonthAheadHeaders>>(`/API/v2/${universeId}/events/list/month_of_data`, { params });

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getMacroEventsWeekAhead = async (countries: string, newFormat = true) => {
    try {
        const res = await axios.get<MacroEventWeekAhead[][] | { obfuscated: boolean; data: MacroEventWeekAhead[][] }>(`/API/v2/0/macro_events/week_ahead${newFormat ? "?new_format" : ""}`, {
            params: {
                countries,
            },
        });

        return {
            obfuscated: res.data["obfuscated"] as boolean,
            data: (Array.isArray(res.data) ? res.data : res.data.data).filter((events) => events.length),
        };
    } catch (err) {
        throw err;
    }
};

export const getMacroEventsByMonth = async (params: { start?: number; length?: number; month_of_year: string; countries?: string; signal?: AbortSignal }) => {
    try {
        const res = await axios.get<List<MacroEventMonthAhead, MacroEventMonthAheadHeaders>>(`/API/v2/0/macro_events/list/month_of_data`, { params });

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getEventTypes = async (universeId: number) => {
    try {
        const res = await axios.get<{ name: string; code: string; enabled: boolean }[]>(`/API/v2/${universeId}/events/event_families`);

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getEventTypesNew = async (): Promise<EventTypesResponse> => {
    try {
        const res = await axios.get<EventTypesResponse>(`/interface/api/events/event_types`);

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getEventsWeekAhead = async (universeId: number, eventTypes: string[], marketCap?: { min: number; max: number }, signal?: AbortSignal) => {
    let url = `/interface/api/events/universe/${universeId}/week_ahead`;
    const params = new URLSearchParams();

    if (eventTypes.length > 0) {
        eventTypes.forEach((type) => params.append("event_type_id", type));
    }

    if (marketCap) {
        params.append("market_cap_min", marketCap.min.toString());
        params.append("market_cap_max", marketCap.max.toString());
    }

    const queryString = params.toString();
    if (queryString) {
        url += `?${queryString}`;
    }

    try {
        const res = await axios.get(url, {
            signal,
        });

        const eventsData = (Array.isArray(res.data) ? res.data : res.data.data)?.filter((events) => events.length);

        // Flatten all events from all days into a single array
        const allEvents: EventWeekAhead[] = eventsData?.flat() || [];

        // Group events by user timezone day using a Map for efficient lookups
        const eventsByUserDay = new Map<string, EventWeekAhead[]>();

        allEvents.forEach((event) => {
            // Convert event date to user timezone

            const eventDate = dayjs.utc(event.date);
            const userTimezoneDate = eventDate.local();

            const dayKey = userTimezoneDate.format("YYYY-MM-DD");

            if (!eventsByUserDay.has(dayKey)) {
                eventsByUserDay.set(dayKey, []);
            }
            eventsByUserDay.get(dayKey)!.push(event);
        });

        eventsByUserDay.forEach((events, dayKey) => {
            events.sort((a, b) => {
                const dateA = dayjs.utc(a.date);
                const dateB = dayjs.utc(b.date);
                return dateA.valueOf() - dateB.valueOf();
            });
        });

        const today = dayjs().startOf("day");
        const displayDays: [string, EventWeekAhead[]][] = [];

        const sixthDay = today.add(5, "day");

        const sixthDayKey = sixthDay.format("YYYY-MM-DD");
        const sixthDayEvents = eventsByUserDay.get(sixthDayKey) || [];

        const getNextMarketDay = (startDate: dayjs.Dayjs): dayjs.Dayjs => {
            let nextDay = startDate;
            while (nextDay.day() === 0 || nextDay.day() === 6) {
                nextDay = nextDay.add(1, "day");
            }
            return nextDay;
        };

        const getPreviousMarketDay = (startDate: dayjs.Dayjs): dayjs.Dayjs => {
            let prevDay = startDate;
            while (prevDay.day() === 0 || prevDay.day() === 6) {
                prevDay = prevDay.subtract(1, "day");
            }
            return prevDay;
        };

        let currentMarketDay = getNextMarketDay(today);

        const actualYesterday = getPreviousMarketDay(today.subtract(1, "day"));
        const actualYesterdayKey = actualYesterday.format("YYYY-MM-DD");
        const actualYesterdayEvents = eventsByUserDay.get(actualYesterdayKey) || [];

        for (let i = 0; i < 5; i++) {
            const dayKey = currentMarketDay.format("YYYY-MM-DD");
            const dayEvents = eventsByUserDay.get(dayKey) || [];

            if (i === 0 && actualYesterdayEvents.length > 0) {
                const todayEvents = actualYesterdayEvents.filter((event) => {
                    const eventDate = dayjs.utc(event.date);
                    const userDate = eventDate.local();
                    return userDate.isAfter(today);
                });
                dayEvents.push(...todayEvents);
            }

            if (i === 4 && sixthDayEvents.length > 0) {
                const lastDayStart = currentMarketDay.startOf("day");
                const lastDayEnd = currentMarketDay.endOf("day");
                const lastDayEvents = sixthDayEvents.filter((event) => {
                    const eventDate = dayjs.utc(event.date);
                    const userDate = eventDate.local();
                    return userDate.isBetween(lastDayStart, lastDayEnd, null, "[]");
                });
                dayEvents.push(...lastDayEvents);
            }

            dayEvents.sort((a, b) => {
                const dateA = dayjs.utc(a.date);
                const dateB = dayjs.utc(b.date);
                return dateA.valueOf() - dateB.valueOf();
            });

            const formattedDate = currentMarketDay.format("dddd, Do MMMM, YYYY");
            displayDays.push([formattedDate, dayEvents]);

            currentMarketDay = getNextMarketDay(currentMarketDay.add(1, "day"));
        }

        return {
            obfuscated: res.data.obfuscated,
            data: displayDays,
            marketCapMin: res.data.marketCapMin,
            marketCapMax: res.data.marketCapMax,
        };
    } catch (err) {
        throw err;
    }
};

export const getEvents = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, next_page_url, universe_id, data, signal } = params;

    const url = next_page_url || `/interface/api/events/universe/${universe_id}/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options, event_types: data.event_types, time_ahead: data.time_ahead };

    try {
        const res = await axios.get<List<EventWeekAhead, EventWeekAheadHeaders>>(url, {
            params: {
                ...defaultParams,
            },
            signal,
        });

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getEventDetails = async (event_id: number, signal?: AbortSignal) => {
    try {
        const res = await axios.get<EventsCallDetails>(`/interface/api/events/${event_id}/call_details`, {
            signal,
        });

        return res.data;
    } catch (err) {
        throw err;
    }
};

export type NoteResponse = { response?: string; obfuscated?: boolean };

// Now reads from /interface/api/market_intelligence/commentary/events/latest/
// — the events note moved into MIContent (content_type=market_commentary,
// theme=events). Wire shape preserved so the /events overview-tab AINote
// rendering doesn't change.
export const getEventsNote = async (): Promise<NoteResponse> => {
    const { getCommentaryLatestAsLegacyNote } = await import("@/controllers/market-intelligence");
    return getCommentaryLatestAsLegacyNote("events");
};
